import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { HostAppShell } from '../components/HostAppShell';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  MapPin, AlertTriangle, CheckCircle, Clock, Eye, 
  Car, Loader2, Shield
} from 'lucide-react';
import { toast } from 'sonner';

const SEVERITY_CONFIG = {
  high: { color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle, iconColor: 'text-red-500' },
  medium: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle, iconColor: 'text-amber-500' },
  low: { color: 'bg-slate-100 text-slate-600 border-slate-200', icon: Shield, iconColor: 'text-slate-400' },
};

const STATUS_CONFIG = {
  active: { color: 'bg-red-50 text-red-600 border-red-200', label: 'Active' },
  acknowledged: { color: 'bg-blue-50 text-blue-600 border-blue-200', label: 'Acknowledged' },
  resolved: { color: 'bg-green-50 text-green-600 border-green-200', label: 'Resolved' },
};

const TYPE_LABELS = {
  movement: 'Unexpected Movement',
  not_returned: 'Not Returned',
  manual_flag: 'Manual Flag',
  geofence: 'Geofence Breach',
  speed: 'Speed Alert',
};

const HostGpsAlertsPage = () => {
  const { api, hasFeature } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [updatingId, setUpdatingId] = useState(null);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const url = filter !== 'all' ? `/host/gps-alerts?status=${filter}` : '/host/gps-alerts';
      const res = await api.get(url);
      setAlerts(res.data.alerts || []);
      setCounts(res.data.counts || {});
    } catch (err) {
      if (err?.response?.status === 403) {
        // Tier-gated — handled by page-level check
      } else {
        toast.error('Could not load GPS alerts');
      }
    }
    setLoading(false);
  };

  useEffect(() => { fetchAlerts(); }, [filter]);

  const handleUpdateStatus = async (alertId, newStatus) => {
    setUpdatingId(alertId);
    try {
      await api.patch(`/host/gps-alerts/${alertId}`, { status: newStatus });
      toast.success(newStatus === 'acknowledged' ? 'Alert acknowledged' : 'Alert resolved');
      fetchAlerts();
    } catch {
      toast.error('Could not update alert');
    }
    setUpdatingId(null);
  };

  // If not entitled, show upgrade prompt (this page shouldn't be reachable for non-Pro, 
  // but defense-in-depth)
  if (!hasFeature('gps_tracking')) {
    return (
      <HostAppShell title="GPS Tracking" activeId="gps_tracking">
        <div className="max-w-lg mx-auto py-12 text-center">
          <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800">GPS Tracking</h2>
          <p className="text-sm text-slate-500 mt-2">GPS alerts and tracking require a Pro plan or above.</p>
          <Button className="mt-4 bg-[#004B8D]" onClick={() => window.location.href = '/host/plan'} data-testid="upgrade-to-pro-btn">
            View Plans
          </Button>
        </div>
      </HostAppShell>
    );
  }

  return (
    <HostAppShell title="GPS Alerts" activeId="gps_tracking">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="gps-alerts-header">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">GPS Alerts</h1>
            <p className="text-sm text-slate-500 mt-1">Monitor and manage alerts for your tracked vehicles.</p>
          </div>
          {counts.active > 0 && (
            <Badge className="bg-red-100 text-red-700 border-red-200 border text-xs">
              {counts.active} active
            </Badge>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2" data-testid="gps-alerts-filters">
          {[
            { id: 'active', label: 'Active', count: counts.active },
            { id: 'acknowledged', label: 'Acknowledged', count: counts.acknowledged },
            { id: 'resolved', label: 'Resolved', count: counts.resolved },
            { id: 'all', label: 'All' },
          ].map(f => (
            <Button
              key={f.id}
              variant={filter === f.id ? 'default' : 'outline'}
              size="sm"
              className={`h-8 text-xs ${filter === f.id ? 'bg-[#004B8D] hover:bg-[#003a6d]' : ''}`}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
              {f.count > 0 && <span className="ml-1.5 opacity-70">({f.count})</span>}
            </Button>
          ))}
        </div>

        {/* Alerts List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#004B8D]" />
          </div>
        ) : alerts.length === 0 ? (
          <Card className="border-dashed border-slate-300" data-testid="no-alerts">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">
                {filter === 'active' ? 'No active alerts' : filter === 'all' ? 'No GPS alerts yet' : `No ${filter} alerts`}
              </p>
              <p className="text-xs text-slate-400 mt-1">Alerts will appear here when GPS events are detected.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="gps-alerts-list">
            {alerts.map(alert => {
              const severity = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.medium;
              const status = STATUS_CONFIG[alert.status] || STATUS_CONFIG.active;
              const SeverityIcon = severity.icon;
              return (
                <Card key={alert.alert_id} className="border-slate-200 hover:shadow-sm transition-shadow" data-testid={`gps-alert-${alert.alert_id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      {/* Alert Info */}
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${alert.severity === 'high' ? 'bg-red-100' : alert.severity === 'medium' ? 'bg-amber-100' : 'bg-slate-100'}`}>
                          <SeverityIcon className={`w-4.5 h-4.5 ${severity.iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-900">{TYPE_LABELS[alert.alert_type] || alert.alert_type}</span>
                            <Badge className={`text-[10px] px-1.5 py-0 h-4 border ${severity.color}`}>
                              {alert.severity}
                            </Badge>
                            <Badge className={`text-[10px] px-1.5 py-0 h-4 border ${status.color}`} data-testid={`alert-status-${alert.alert_id}`}>
                              {status.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-0.5">{alert.message}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                            <span className="flex items-center gap-1"><Car className="w-3 h-3" />{alert.vehicle_name || alert.vehicle_id}</span>
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(alert.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {alert.status === 'active' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                            onClick={() => handleUpdateStatus(alert.alert_id, 'acknowledged')}
                            disabled={updatingId === alert.alert_id}
                            data-testid={`acknowledge-${alert.alert_id}`}
                          >
                            {updatingId === alert.alert_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                            Acknowledge
                          </Button>
                        )}
                        {(alert.status === 'active' || alert.status === 'acknowledged') && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs border-green-200 text-green-700 hover:bg-green-50"
                            onClick={() => handleUpdateStatus(alert.alert_id, 'resolved')}
                            disabled={updatingId === alert.alert_id}
                            data-testid={`resolve-${alert.alert_id}`}
                          >
                            {updatingId === alert.alert_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                            Resolve
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </HostAppShell>
  );
};

export default HostGpsAlertsPage;
